import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, MapPin } from 'lucide-react';
import { jobsApi, categoriesApi } from '../services/api';
import type { Job, Category, JobFilters } from '../types';
import { JobCard } from '../components/jobs/JobCard';
import { SkeletonCard } from '../components/ui/Skeleton';
import { Button } from '../components/ui/Button';

export const JobList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<JobFilters>({
    search: searchParams.get('search') || '',
    category_id: searchParams.get('category_id') ? Number(searchParams.get('category_id')) : undefined,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
  });

  const fetchJobs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { ...filters, page, per_page: 12 };
      const { data } = await jobsApi.list(params);
      setJobs(data.data);
      setPagination({
        current_page: data.current_page,
        last_page: data.last_page,
        total: data.total,
      });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    categoriesApi.list().then((res) => setCategories(res.data));
  }, []);

  const applyFilters = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        params.set(key, String(value));
      }
    });
    setSearchParams(params);
    fetchJobs(1);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchParams({});
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar Filters */}
        <aside className={`lg:w-72 ${showFilters ? 'block' : 'hidden lg:block'}`}>
          <div className="bg-white rounded-xl border border-secondary-200 p-5 sticky top-20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-secondary-900">Filtros</h3>
              <button onClick={clearFilters} className="text-sm text-primary-600 hover:text-primary-700">
                Limpiar
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="label">Categoría</label>
                <select
                  value={filters.category_id || ''}
                  onChange={(e) => setFilters((f) => ({ ...f, category_id: e.target.value ? Number(e.target.value) : undefined }))}
                  className="input"
                >
                  <option value="">Todas</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Presupuesto mínimo</label>
                <input
                  type="number"
                  value={filters.budget_min || ''}
                  onChange={(e) => setFilters((f) => ({ ...f, budget_min: e.target.value ? Number(e.target.value) : undefined }))}
                  className="input"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="label">Presupuesto máximo</label>
                <input
                  type="number"
                  value={filters.budget_max || ''}
                  onChange={(e) => setFilters((f) => ({ ...f, budget_max: e.target.value ? Number(e.target.value) : undefined }))}
                  className="input"
                  placeholder="Sin límite"
                />
              </div>

              <div>
                <label className="label">Nivel de experiencia</label>
                <select
                  value={filters.experience_level || ''}
                  onChange={(e) => setFilters((f) => ({ ...f, experience_level: e.target.value || undefined }))}
                  className="input"
                >
                  <option value="">Todos</option>
                  <option value="beginner">Principiante</option>
                  <option value="intermediate">Intermedio</option>
                  <option value="expert">Experto</option>
                </select>
              </div>

              <div>
                <label className="label">Duración</label>
                <select
                  value={filters.duration || ''}
                  onChange={(e) => setFilters((f) => ({ ...f, duration: e.target.value || undefined }))}
                  className="input"
                >
                  <option value="">Todas</option>
                  <option value="less_than_1_week">Menos de 1 semana</option>
                  <option value="1_to_2_weeks">1 a 2 semanas</option>
                  <option value="2_to_4_weeks">2 a 4 semanas</option>
                  <option value="1_to_3_months">1 a 3 meses</option>
                  <option value="more_than_3_months">Más de 3 meses</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="urgent"
                  checked={filters.is_urgent || false}
                  onChange={(e) => setFilters((f) => ({ ...f, is_urgent: e.target.checked || undefined }))}
                  className="rounded border-secondary-300 text-primary-600"
                />
                <label htmlFor="urgent" className="text-sm text-secondary-700">Solo urgentes</label>
              </div>

              <Button variant="primary" className="w-full" onClick={applyFilters}>
                Aplicar Filtros
              </Button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
              <input
                type="text"
                value={filters.search || ''}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                placeholder="Buscar trabajos..."
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-secondary-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden p-3 rounded-xl border border-secondary-200 bg-white text-secondary-600"
            >
              <SlidersHorizontal className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-secondary-500">
              {pagination.total} trabajos encontrados
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
              : jobs.map((job) => <JobCard key={job.id} job={job} />)}
          </div>

          {!loading && jobs.length === 0 && (
            <div className="text-center py-16">
              <MapPin className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-700">No se encontraron trabajos</h3>
              <p className="text-secondary-500 mt-1">Intenta con otros filtros o términos de búsqueda</p>
            </div>
          )}

          {pagination.last_page > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => fetchJobs(page)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    page === pagination.current_page
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-secondary-600 border border-secondary-200 hover:bg-secondary-50'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
